import { useState, memo } from 'react';
import { useCursor, Html } from '@react-three/drei';
import { RigidBody } from '@react-three/rapier';
import { useStore } from '../../store/useStore';
import { useAudio } from '../../hooks/useAudio';

interface InteractableProps {
    id?: string; // Server ID
    position: [number, number, number];
    name: string;
    color?: string;
    onInteract?: () => void;
}

export const Interactable = memo(({ id, position, name, color = 'gold', onInteract }: InteractableProps) => {
    const [hovered, setHovered] = useState(false);

    // We rely on the parent (World) to remove us if we are collected on server.
    // Locally, we just send the request.

    useCursor(hovered);
    const { playSfx } = useAudio();

    const handleClick = () => {
        playSfx('click');
        if (onInteract) {
            onInteract();
            return;
        }

        // Server Authority Pickup
        if (id) {
            console.log("Attempting pickup for item:", id, name);
            const room = useStore.getState().room; // We need to access room to send message
            if (room) {
                room.send("pickup", { itemId: id });
                // Optimistic update or wait for server?
                // Let's wait for server removal to unmount us.
                // But we can add to local inventory immediately if we trust success?
                // For now, let's just send logic.
                // Actually, we should only add to inventory if server says yes? 
                // Plan said "Server rejects". 
                // We will listen to "itemCollected" message in World or here?
                // Simple approach: Send message. Server removes item. World re-renders (item gone). 
                // But we need to add to inventory!
                // We can listen to a broadcast or just add it optimistically.
                // Server will confirm pickup and drive inventory updates.
            }
        }
    };



    return (
        <RigidBody 
            position={position} 
            colliders="cuboid" 
            userData={{ type: 'interactable', id: id, onInteract: handleClick }}
            collisionGroups={0x0002FFFF} // Ensure it's in the group the player can interact with
            lockRotations // Keep item upright
            linearDamping={2.0} // Prevent sliding forever
            angularDamping={2.0}
        >
            <mesh
                onClick={(e) => {
                    e.stopPropagation(); // Stop click from hitting terrain
                    handleClick();
                }}
                onPointerOver={() => {
                    document.body.style.cursor = 'pointer';
                    setHovered(true);
                }}
                onPointerOut={() => {
                    document.body.style.cursor = 'auto';
                    setHovered(false);
                }}
                castShadow
                receiveShadow
            >
                <boxGeometry args={[1, 1, 1]} />
                <meshStandardMaterial
                    color={hovered ? 'white' : color}
                    roughness={0.3}
                    metalness={0.8}
                    emissive={hovered ? 'white' : 'black'}
                    emissiveIntensity={hovered ? 0.5 : 0}
                />

                {hovered && (
                    <Html position={[0, 1.2, 0]} center distanceFactor={10}>
                        <div style={{
                            background: '#333',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none'
                        }}>
                            {name}
                            <div style={{ fontSize: '10px', color: '#aaa' }}>(Click to pickup)</div>
                        </div>
                    </Html>
                )}
            </mesh>
        </RigidBody>
    );
});
